// ──────────────────────────────────────────────────────────────────────────
//  WorkflowCodeTab
//  =================================================================
//  Right-panel "Code" tab — Conductor OSS-parity full-workflow JSON editor,
//  bidirectional. Serves three workflows:
//
//    (1) VIEW — paint the current canvas state as pretty-printed JSON so
//        engineers can inspect the exact `{nodes, connections}` the
//        renderer is working from, without a backend round-trip.
//
//    (2) PASTE-TO-RENDER — paste another workflow's Canvas-native JSON
//        (exported from a golden fixture, another workspace, a test
//        case, etc.) and see the DAG re-layout on commit. Invaluable
//        when chasing layout/topology bugs against fixtures.
//
//    (3) LOCAL-EDIT — hand-tweak a node's position, type, or config in
//        JSON, commit, and watch the canvas update. Faster than the
//        properties panel for bulk changes or obscure fields.
//
//  The tab deliberately consumes/produces Canvas-native flat JSON
//  (`{nodes: [...], connections: [...]}`), NOT Conductor OSS format.
//  `canvas.loadCanvasData` already accepts this shape, so the edit
//  loop is:   edit → JSON.parse → onApply(parsed) → loadCanvasData
//                         ↓
//                    debounced 600ms
//
//  Conductor OSS-format paste is a P2 follow-up — it requires a backend round
//  trip through `ConductorToCanvasConverter` (see the existing
//  POST /api/v1/discovery/workflows/{id}/convert-native/preview
//  endpoint). When we add that, this component gains a format toggle:
//  [ Canvas native | Conductor OSS source ] at the top of the editor.
// ──────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState, lazy, Suspense } from 'react';
import { Copy, Check, AlertTriangle, RotateCcw, FileCode2 } from 'lucide-react';

// Lazy-load Monaco so the canvas first-paint doesn't wait on the
// ~2 MB editor bundle. Users only incur the download when they click
// the Code tab. Falls back to a styled <textarea> while loading or
// if the package can't resolve (e.g. offline dev without node_modules
// refreshed).
const MonacoEditor = lazy(() =>
  import('@monaco-editor/react')
    .then((mod) => ({ default: mod.default || mod.Editor || mod }))
    .catch(() => ({
      // Graceful fallback component — passes through to the Fallback
      // textarea branch below via a null render.
      default: () => null,
    })),
);

// ─── helpers ─────────────────────────────────────────────────────────────

/**
 * Serialize canvas state to a stable, diff-friendly JSON string. Key
 * order is deterministic so external state updates don't churn the
 * editor cursor when the user hasn't touched anything.
 */
function canonicalize(nodes, connections, workflowMeta) {
  const safeNodes = Array.isArray(nodes) ? nodes : [];
  const safeConns = Array.isArray(connections) ? connections : [];
  const payload = {
    ...(workflowMeta && typeof workflowMeta === 'object' ? {
      name: workflowMeta.name,
      version: workflowMeta.version,
      description: workflowMeta.description,
    } : {}),
    nodes: safeNodes,
    connections: safeConns,
  };
  return JSON.stringify(payload, null, 2);
}

/**
 * Validate a parsed object as Canvas-native flat JSON. Returns null
 * on success, or a short error string otherwise.
 */
function validateShape(parsed) {
  if (parsed == null || typeof parsed !== 'object') {
    return 'Top-level JSON must be an object';
  }
  if (!Array.isArray(parsed.nodes)) {
    return 'Missing or non-array `nodes`';
  }
  if (!Array.isArray(parsed.connections)) {
    return 'Missing or non-array `connections`';
  }
  for (let i = 0; i < parsed.nodes.length; i++) {
    const n = parsed.nodes[i];
    if (!n || typeof n !== 'object') return `nodes[${i}] is not an object`;
    if (typeof n.id !== 'string' || !n.id) return `nodes[${i}].id must be a non-empty string`;
    if (typeof n.type !== 'string') return `nodes[${i}].type must be a string`;
  }
  for (let i = 0; i < parsed.connections.length; i++) {
    const c = parsed.connections[i];
    if (!c || typeof c !== 'object') return `connections[${i}] is not an object`;
    const from = c.from || c.source;
    const to = c.to || c.target;
    if (typeof from !== 'string' || !from) return `connections[${i}] needs a string \`from\` or \`source\``;
    if (typeof to !== 'string' || !to) return `connections[${i}] needs a string \`to\` or \`target\``;
  }
  return null;
}

// ─── component ────────────────────────────────────────────────────────────

export default function WorkflowCodeTab({
  nodes,
  connections,
  workflowMeta,
  onApply,              // (parsed) => void
  onValidityChange,     // (error | null) => void — lifted so toolbar Save/Execute can mirror Conductor OSS "halt on invalid JSON"
  readOnly = false,
  onClose,              // optional — if present we render no tab chrome, just the editor
}) {
  // Canonical serialization of current canvas state. Updates any time
  // nodes/connections/meta change externally (drag, Properties-panel
  // edit, new workflow loaded, etc.).
  const canonical = useMemo(
    () => canonicalize(nodes, connections, workflowMeta),
    [nodes, connections, workflowMeta],
  );

  const [editorValue, setEditorValue] = useState(canonical);
  const [parseError, setParseError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [lastAppliedAt, setLastAppliedAt] = useState(null);

  // Track dirtiness so we don't clobber user edits when the canvas
  // state updates externally. If the editor buffer matches the LAST
  // canonical we pushed, we sync; otherwise we keep the user's draft
  // and they can Revert to discard.
  const lastSyncedCanonicalRef = useRef(canonical);
  useEffect(() => {
    if (editorValue === lastSyncedCanonicalRef.current) {
      lastSyncedCanonicalRef.current = canonical;
      setEditorValue(canonical);
      setParseError(null);
    } else {
      // User has unsaved edits — just update the watermark so
      // "Revert" will restore to the latest canonical.
      lastSyncedCanonicalRef.current = canonical;
    }
  }, [canonical]); // eslint-disable-line

  // ─── Workflow-context change → force-reset, dirty-edit-protection
  // notwithstanding ─────────────────────────────────────────────────────
  //
  // Bug repro (2026-05-05): a user pastes a custom JSON snippet into
  // the editor (creating a "dirty" buffer that the canonical-watcher
  // above rightly preserves), then deletes the workflow. The
  // surrounding canvas resets to a Start→End sentinel — but the
  // editor still shows the old paste, because dirty-edit protection
  // refused to clobber it. The buffer is now referring to a workflow
  // that no longer exists, which is worse than losing the draft.
  //
  // The fix is a workflow-identity tracker: any time {@code workflowMeta.id}
  // transitions from one value to another (including
  // {@code defined → null} on delete and {@code null → defined} on
  // load), the editor force-syncs to the new canonical. Dirty edits
  // are sacrificed only on identity change — which is the moment they
  // stop being meaningful anyway.
  const prevWorkflowIdRef = useRef(workflowMeta?.id ?? null);
  useEffect(() => {
    const nextId = workflowMeta?.id ?? null;
    if (prevWorkflowIdRef.current !== nextId) {
      prevWorkflowIdRef.current = nextId;
      lastSyncedCanonicalRef.current = canonical;
      setEditorValue(canonical);
      setParseError(null);
      setLastAppliedAt(null);
    }
  }, [workflowMeta?.id, canonical]);

  const dirty = editorValue !== lastSyncedCanonicalRef.current;

  // Mirror parse-error state upward so the parent toolbar can disable
  // Save / Execute while the JSON is malformed — Conductor OSS parity: invalid
  // JSON halts DAG formation AND blocks persist/run until fixed.
  // On unmount (user switched to a different tab), we explicitly clear
  // the lifted error so Save/Execute aren't stuck disabled by a draft
  // the user walked away from — the canvas is always in a known-good
  // state because we never apply invalid JSON to it.
  useEffect(() => {
    if (onValidityChange) onValidityChange(parseError);
  }, [parseError, onValidityChange]);
  useEffect(() => {
    return () => { if (onValidityChange) onValidityChange(null); };
  }, []); // eslint-disable-line

  // Debounced commit: on quiet, parse + validate + push to canvas.
  // 600 ms is the sweet spot — long enough to coalesce typing bursts,
  // short enough that "paste → render" feels instant.
  useEffect(() => {
    if (!dirty) {
      setParseError(null);
      return undefined;
    }
    if (readOnly) return undefined;

    const t = setTimeout(() => {
      let parsed;
      try {
        parsed = JSON.parse(editorValue);
      } catch (e) {
        setParseError(`JSON parse: ${e.message}`);
        return;
      }
      const shapeErr = validateShape(parsed);
      if (shapeErr) {
        setParseError(shapeErr);
        return;
      }
      setParseError(null);
      try {
        onApply && onApply(parsed);
        setLastAppliedAt(Date.now());
        lastSyncedCanonicalRef.current = canonicalize(
          parsed.nodes,
          parsed.connections,
          workflowMeta,
        );
      } catch (e) {
        setParseError(`Apply failed: ${e.message}`);
      }
    }, 600);

    return () => clearTimeout(t);
  }, [editorValue, dirty, readOnly]); // eslint-disable-line

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(editorValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      // noop — clipboard API may be unavailable in some iframes
    }
  };

  const handleRevert = () => {
    setEditorValue(lastSyncedCanonicalRef.current);
    setParseError(null);
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(editorValue);
      setEditorValue(JSON.stringify(parsed, null, 2));
    } catch (_) {
      // unformatable — leave as-is; debounced parse will surface the error
    }
  };

  const statusLabel = readOnly
    ? 'Read-only'
    : parseError
      ? 'Invalid'
      : dirty
        ? 'Applying…'
        : lastAppliedAt
          ? 'Applied'
          : 'In sync';
  const statusColor = readOnly
    ? 'var(--v2-text-muted)'
    : parseError
      ? 'var(--v2-danger)'
      : dirty
        ? 'var(--v2-warning)'
        : 'var(--v2-success)';

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: 'var(--v2-bg-surface)',
      fontFamily: 'Lexend, Inter, system-ui, sans-serif',
    }}>
      {/* ─── Toolbar ────────────────────────────────────────────────── */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 12px',
        borderBottom: '1px solid var(--v2-bg-muted)',
        gap: 8,
        minHeight: 40,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--v2-text-secondary)', fontSize: 12 }}>
          <FileCode2 size={14} style={{ color: 'var(--v2-text-muted)' }} />
          <span style={{ fontWeight: 500 }}>
            {(nodes || []).length} nodes · {(connections || []).length} edges
          </span>
          <span style={{
            color: statusColor,
            fontSize: 11,
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: 10,
            background: parseError ? 'var(--v2-danger-soft)' : dirty ? 'var(--v2-warning-soft)' : 'var(--v2-success-soft)',
            border: `1px solid ${parseError ? 'var(--v2-danger-soft)' : dirty ? 'var(--v2-warning-soft)' : 'var(--v2-success-soft)'}`,
          }}>
            {statusLabel}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          <button
            onClick={handleFormat}
            title="Format JSON"
            disabled={readOnly}
            style={toolbarBtnStyle(readOnly)}
          >
            { }
            <span style={{ fontSize: 11, fontWeight: 600, padding: '0 2px' }}>{`{ }`}</span>
          </button>
          <button
            onClick={handleRevert}
            title="Revert to current canvas state"
            disabled={!dirty || readOnly}
            style={toolbarBtnStyle(!dirty || readOnly)}
          >
            <RotateCcw size={13} />
          </button>
          <button
            onClick={handleCopy}
            title="Copy to clipboard"
            style={toolbarBtnStyle(false)}
          >
            {copied ? <Check size={13} style={{ color: 'var(--v2-success)' }} /> : <Copy size={13} />}
          </button>
        </div>
      </div>

      {/* ─── Error banner ───────────────────────────────────────────── */}
      {parseError && (
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 6,
          padding: '8px 12px',
          background: 'var(--v2-danger-soft)',
          borderBottom: '1px solid var(--v2-danger-soft)',
          color: 'var(--v2-danger)',
          fontSize: 11,
          lineHeight: 1.4,
        }}>
          <AlertTriangle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{parseError}</div>
        </div>
      )}

      {/* ─── Editor ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
        <Suspense fallback={
          <FallbackTextarea
            value={editorValue}
            onChange={setEditorValue}
            readOnly={readOnly}
          />
        }>
          <MonacoEditor
            language="json"
            theme="vs"
            value={editorValue}
            onChange={(v) => setEditorValue(v ?? '')}
            options={{
              readOnly,
              minimap: { enabled: true, renderCharacters: false, maxColumn: 80 },
              lineNumbers: 'on',
              folding: true,
              wordWrap: 'off',
              tabSize: 2,
              insertSpaces: true,
              automaticLayout: true,
              fontSize: 12,
              fontFamily:
                'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
              scrollBeyondLastLine: false,
              renderLineHighlight: 'gutter',
              smoothScrolling: true,
              cursorSmoothCaretAnimation: 'on',
              padding: { top: 8, bottom: 8 },
            }}
            loading={<FallbackTextarea value={editorValue} onChange={setEditorValue} readOnly={readOnly} />}
          />
        </Suspense>
      </div>
    </div>
  );
}

// ─── styling helpers ─────────────────────────────────────────────────────

function toolbarBtnStyle(disabled) {
  return {
    width: 24,
    height: 24,
    padding: 0,
    border: '1px solid var(--v2-border-default)',
    borderRadius: 6,
    background: 'var(--v2-bg-surface)',
    color: disabled ? 'var(--v2-border-default)' : 'var(--v2-text-secondary)',
    cursor: disabled ? 'not-allowed' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: disabled ? 0.6 : 1,
  };
}

// ─── fallback editor ─────────────────────────────────────────────────────

/**
 * Monospaced textarea used while Monaco lazy-loads or if the package
 * fails to resolve (e.g. `npm install` hasn't been run since the
 * dependency was added). Functional but without syntax highlighting,
 * minimap, or folding. The bidirectional paste-to-render loop still
 * works since the parse/validate/apply chain lives in the parent.
 */
function FallbackTextarea({ value, onChange, readOnly }) {
  return (
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      readOnly={readOnly}
      spellCheck={false}
      style={{
        width: '100%',
        height: '100%',
        border: 'none',
        outline: 'none',
        padding: '10px 12px',
        fontSize: 12,
        lineHeight: 1.5,
        fontFamily:
          'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
        color: 'var(--v2-text-primary)',
        background: 'var(--v2-bg-surface)',
        resize: 'none',
        tabSize: 2,
      }}
    />
  );
}
